import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  cleanRule, countryName, groupAsksItsOwn, LETTER_FRAMES, letterFrame, MAX_FOLLOW_UPS, ruleIsEmpty, ruleSays,
  type GroupLetterView, type GroupView, type LetterRule, type SetupResponse,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { Card } from "./setup/chrome.js";
import { LetterSheet } from "./setup/Letter.js";

/**
 * **A group's Letters tab** (38 step 2, ADR 0068 point 3): several letters, read top to bottom.
 *
 * Each has a rule saying who gets it, over what Joogoos already knows about a company — its country, its trade or
 * what its site says, its size and the role of the person a list named. The first whose rule takes a company is
 * the one it gets, and **the last is for everyone else**, so its rule is not asked for. A company keeps the letter it
 * got, so changing a rule here changes who gets which from now on, never what anybody was already told.
 *
 * A business with one group and one letter sees one letter, as it did before: the rule and the order only appear
 * once there is a second.
 */
export function GroupLetters({ g, d, keep }: { g: GroupView; d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const add = useMutation({ mutationFn: () => api.addLetter(g.id, { name: "" }), onSuccess: keep });
  // A letter's second version sits under it, not in the order (38 step 5).
  const own = g.letters.filter((l) => !l.versionOf);
  const several = own.length > 1;
  return (
    <div className="flex flex-col gap-3.5 max-w-[790px]">
      {several ? (
        <p className="text-[12px] text-muted leading-relaxed">
          Read from the top: a company gets the first letter whose rule takes it, and the last one if none does.
          A company keeps the letter it got, so a change here is for the companies written to from now on.
        </p>
      ) : null}
      {own.map((l, i) => (
        <OneLetter key={l.id} g={g} d={d} keep={keep} letter={l} n={i + 1} last={i === own.length - 1} several={several}
          version={g.letters.find((v) => v.versionOf === l.id)} />
      ))}
      {d.mayEdit && g.state !== "dropped" ? (
        <button onClick={() => add.mutate()} disabled={add.isPending}
          className="self-start px-3 py-1.5 rounded-lg border border-line-2 bg-page text-[12px] font-medium disabled:opacity-50"
        >Add a letter for some of these companies</button>
      ) : null}
      {add.error ? <p className="text-xs text-bad">{String(add.error)}</p> : null}
    </div>
  );
}

function OneLetter({ g, d, keep, letter, n, last, several, version }: {
  g: GroupView; d: SetupResponse; keep: (d: SetupResponse) => void; letter: GroupLetterView; n: number; last: boolean; several: boolean;
  version?: GroupLetterView | undefined;
}) {
  const split = useMutation({ mutationFn: () => api.addVersion(letter.id), onSuccess: keep });
  const save = useMutation({ mutationFn: (patch: Record<string, unknown>) => api.saveLetter(letter.id, patch), onSuccess: keep });
  const move = useMutation({ mutationFn: (by: "up" | "down") => api.moveLetter(letter.id, by), onSuccess: keep });
  const remove = useMutation({ mutationFn: () => api.removeLetter(letter.id), onSuccess: keep });
  const [offer, setOffer] = useState(letter.offer);
  const [ask, setAsk] = useState(letter.ask);
  const ownAsk = groupAsksItsOwn(g.route);
  const usual = letterFrame(d.settings.languages[0] ?? "en").name;
  const own = Boolean(letter.offer.trim() || letter.ask.trim() || letter.language);
  const busy = save.isPending || move.isPending || remove.isPending;
  const may = d.mayEdit && g.state !== "dropped";
  const title = !several ? "This group's letter"
    : letter.name || (last ? "For everyone else" : ruleIsEmpty(letter.rule) ? `Letter ${n}` : `For ${ruleSays(letter.rule, countryName)}`);

  return (
    <Card title={title} aside={<>
      <span className="text-xs text-muted">
        {own ? "its own words, and your usual letter's where it says nothing" : "your usual letter, until you write its own here"}
        {letter.companies ? ` · ${letter.companies} ${letter.companies === 1 ? "company has" : "companies have"} it` : ""}
      </span>
      {may && several ? (
        <span className="ml-auto flex items-center gap-1">
          <button aria-label={`Move ${title} up`} disabled={busy || n === 1} onClick={() => move.mutate("up")} className="px-1.5 text-xs text-muted disabled:opacity-30">↑</button>
          <button aria-label={`Move ${title} down`} disabled={busy || last} onClick={() => move.mutate("down")} className="px-1.5 text-xs text-muted disabled:opacity-30">↓</button>
          <button disabled={busy} onClick={() => remove.mutate()} className="px-1.5 text-xs font-medium text-coral disabled:opacity-30">Remove</button>
        </span>
      ) : null}
    </>}>
      {several ? (
        <div className="flex flex-col gap-2 rounded-lg border border-line-2 bg-panel/60 p-2.5">
          <label className="flex items-center gap-2">
            <span className="w-[86px] text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Name</span>
            <input aria-label="Its name" defaultValue={letter.name} disabled={!may || busy} placeholder="named by its rule"
              onBlur={(e) => e.target.value.trim() !== letter.name && save.mutate({ name: e.target.value })}
              className="flex-1 px-2.5 py-1 rounded-lg border border-line-2 bg-panel text-[12.5px]" />
          </label>
          {last ? (
            <p className="text-[12px] text-muted">Who gets it: every company the letters above do not take.</p>
          ) : (
            <Rule rule={letter.rule} places={g.countries} disabled={!may || busy} onSave={(rule) => save.mutate({ rule })} />
          )}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Written in</span>
          <select
            aria-label="Written in" value={letter.language} disabled={!may || busy}
            onChange={(e) => save.mutate({ language: e.target.value })}
            className="px-2.5 py-1 rounded-lg border border-line-2 bg-panel text-[13px]"
          >
            <option value="">as your usual letter ({usual})</option>
            {LETTER_FRAMES.map((f) => <option key={f.language} value={f.language}>{f.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Follow-ups</span>
          <select
            aria-label="Follow-ups" value={letter.followUps} disabled={!may || busy}
            onChange={(e) => save.mutate({ followUps: Number(e.target.value) })}
            className="px-2.5 py-1 rounded-lg border border-line-2 bg-panel text-[13px]"
          >
            {Array.from({ length: MAX_FOLLOW_UPS + 1 }, (_, k) => <option key={k} value={k}>{k === 0 ? "none" : k}</option>)}
          </select>
        </label>
      </div>
      {ownAsk ? (
        <p className="text-xs text-muted leading-relaxed">
          This group is reached through the businesses that hold the people you want, so its letter asks them something
          of its own. Without an ask, nothing is drafted for it.
        </p>
      ) : null}
      <LetterSheet
        d={d} language={letter.language || d.settings.languages[0] || "en"} mayEdit={may}
        offer={offer} ask={ask} onOffer={setOffer} onAsk={setAsk}
        onOfferDone={() => offer.trim() !== letter.offer && save.mutate({ offer: offer.trim() })}
        onAskDone={() => ask.trim() !== letter.ask && save.mutate({ ask: ask.trim() })}
        fallback={{ offer: d.settings.offer, ask: ownAsk ? "" : d.settings.ask }}
      />
      {save.error || move.error || remove.error ? <p className="text-xs text-bad">{String(save.error ?? move.error ?? remove.error)}</p> : null}
      {version ? (
        <Version g={g} d={d} keep={keep} letter={letter} version={version} may={may} />
      ) : may ? (
        <button onClick={() => split.mutate()} disabled={split.isPending}
          className="self-start text-xs font-medium text-coral disabled:opacity-50">Try a second version of this letter</button>
      ) : null}
      {split.error ? <p className="text-xs text-bad">{String(split.error)}</p> : null}
    </Card>
  );
}

/** Answered of written, for one side of a test. */
const tally = (l: GroupLetterView) => `${l.answered} of ${l.written} answered`;

/**
 * A letter's second version (38 step 5): its own words, the letter's companies shared 50/50 between the two, and the
 * numbers of each side. The test is called on Today, and only once one side is clearly ahead; ending it here keeps
 * the first version.
 */
function Version({ g, d, keep, letter, version, may }: {
  g: GroupView; d: SetupResponse; keep: (d: SetupResponse) => void; letter: GroupLetterView; version: GroupLetterView; may: boolean;
}) {
  const save = useMutation({ mutationFn: (patch: Record<string, unknown>) => api.saveLetter(version.id, patch), onSuccess: keep });
  const end = useMutation({ mutationFn: () => api.removeLetter(version.id), onSuccess: keep });
  const [offer, setOffer] = useState(version.offer);
  const [ask, setAsk] = useState(version.ask);
  const ownAsk = groupAsksItsOwn(g.route);
  return (
    <section aria-label="Its second version" className="flex flex-col gap-2.5 rounded-lg border border-dashed border-line-2 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[12.5px] font-semibold">Second version</h3>
        <span className="text-xs text-muted">half its companies get this one · first: {tally(letter)} · second: {tally(version)}</span>
        {may ? <button onClick={() => end.mutate()} disabled={end.isPending} className="ml-auto text-xs font-medium text-coral disabled:opacity-50">End the test, keep the first</button> : null}
      </div>
      <p className="text-[11.5px] text-faint">Today asks you to call it once one version is clearly ahead, and not before.</p>
      <LetterSheet
        d={d} language={version.language || d.settings.languages[0] || "en"} mayEdit={may && !save.isPending}
        offer={offer} ask={ask} onOffer={setOffer} onAsk={setAsk}
        onOfferDone={() => offer.trim() !== version.offer && save.mutate({ offer: offer.trim() })}
        onAskDone={() => ask.trim() !== version.ask && save.mutate({ ask: ask.trim() })}
        fallback={{ offer: d.settings.offer, ask: ownAsk ? "" : d.settings.ask }}
      />
      {save.error || end.error ? <p className="text-xs text-bad">{String(save.error ?? end.error)}</p> : null}
    </section>
  );
}

const words = (v: string) => v.split(",").map((w) => w.trim()).filter(Boolean);
const num = (v: string) => (v.trim() === "" || !Number.isFinite(Number(v)) ? null : Math.max(0, Math.trunc(Number(v))));

/** Who gets a letter: the parts of a rule, each only what Joogoos already knows. Saved as each box is left. */
function Rule({ rule, places, disabled, onSave }: { rule: LetterRule; places: string[]; disabled: boolean; onSave: (r: LetterRule) => void }) {
  const countries = [...new Set(places.map((p) => p.slice(0, 2)))];
  const put = (patch: Partial<LetterRule>) => {
    const next = cleanRule({ ...rule, ...patch });
    if (JSON.stringify(next) !== JSON.stringify(cleanRule(rule))) onSave(next);
  };
  return (
    <div className="flex flex-col gap-1.5 text-[12.5px]">
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Who gets it</span>
      {countries.length > 1 ? (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="w-[86px] text-muted">In</span>
          {countries.map((c) => {
            const on = rule.countries?.includes(c) ?? false;
            return (
              <button key={c} type="button" disabled={disabled} aria-pressed={on}
                onClick={() => put({ countries: on ? (rule.countries ?? []).filter((x) => x !== c) : [...(rule.countries ?? []), c] })}
                className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${on ? "bg-ink text-white border-ink" : "bg-panel border-line-2 text-muted"}`}
              >{countryName(c)}</button>
            );
          })}
          <span className="text-[11px] text-faint">{rule.countries?.length ? "" : "any of them"}</span>
        </span>
      ) : null}
      <label className="flex items-center gap-2">
        <span className="w-[86px] text-muted">Its kind</span>
        <input aria-label="Its kind" defaultValue={(rule.kinds ?? []).join(", ")} disabled={disabled} placeholder="any — or words such as Shopify, florist"
          onBlur={(e) => put({ kinds: words(e.target.value) })} className="flex-1 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
      </label>
      <span className="flex items-center gap-2">
        <span className="w-[86px] text-muted">People</span>
        <input aria-label="At least this many people" defaultValue={rule.sizeMin ?? ""} disabled={disabled} inputMode="numeric" placeholder="from"
          onBlur={(e) => put({ sizeMin: num(e.target.value) })} className="w-20 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
        <input aria-label="At most this many people" defaultValue={rule.sizeMax ?? ""} disabled={disabled} inputMode="numeric" placeholder="to"
          onBlur={(e) => put({ sizeMax: num(e.target.value) })} className="w-20 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
      </span>
      <label className="flex items-center gap-2">
        <span className="w-[86px] text-muted">To the</span>
        <input aria-label="Their role" defaultValue={(rule.roles ?? []).join(", ")} disabled={disabled} placeholder="anyone — or roles such as buyer, owner"
          onBlur={(e) => put({ roles: words(e.target.value) })} className="flex-1 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
      </label>
      <span className="text-[11px] text-faint leading-relaxed">
        Its kind is read in a list's trade and in what was read on its site; its size and the role only come with a list.
        A company nobody said the size of does not get a letter for a size.
      </span>
    </div>
  );
}
